// src/components/VerifyEmail.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Login.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

interface VerifyEmailResponse {
  success: boolean;
  message: string;
  email?: string;
}

interface VerifyEmailProps {
  onVerificationComplete: () => void;
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({ onVerificationComplete }) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      // Get token from URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await axios.post<VerifyEmailResponse>(`${API_BASE_URL}/auth/verify-email`, {
          token: token
        });

        const data = response.data;
        if (data.success) {
          setStatus('success');
          setMessage(data.message);
          setEmail(data.email || '');
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed.');
        }
      } catch (error: any) {
        setStatus('error');
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
        } else {
          setMessage('Verification failed. Please try again or request a new link.');
        }
      }
    };

    verifyEmail();
  }, []);

  const handleContinueToLogin = () => {
    // Clear the URL params and redirect to login
    window.history.replaceState({}, document.title, '/');
    onVerificationComplete();
  };

  return (
    <div className="login-page">
      <div className="login-background-dots"></div>
      <div className="login-card">
        {/* Header with logo and title */}
        <div className="login-logo-section">
          <div className="login-title-row">
            <img src="/logo.png" alt="CareShift" className="login-logo-image" />
            <h1 className="login-title">CareShift</h1>
          </div>
          <p className="login-subtitle">Intelligent Nurse Handoff Assistant</p>
        </div>

        {/* Status content */}
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          {status === 'loading' && (
            <>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                background: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <div className="login-spinner" style={{ 
                  width: '32px', 
                  height: '32px',
                  borderColor: 'rgba(255,255,255,0.3)',
                  borderTopColor: 'white'
                }}></div>
              </div>
              <h2 style={{ 
                color: '#1e293b', 
                fontSize: '1.5rem', 
                fontWeight: 700, 
                marginBottom: '0.75rem' 
              }}>
                Verifying Email
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                background: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <h2 style={{ 
                color: '#1e293b', 
                fontSize: '1.5rem', 
                fontWeight: 700, 
                marginBottom: '0.75rem' 
              }}>
                Email Verified!
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                {email && <>Your email <strong>{email}</strong> has been verified.</>}
                {!email && 'Your email has been verified successfully.'}
              </p>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '2rem' }}>
                You can now sign in to your account.
              </p>
              <button 
                onClick={handleContinueToLogin}
                className="login-submit-btn"
                style={{ width: '100%' }}
              >
                Back to Login
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                background: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </div>
              <h2 style={{ 
                color: '#1e293b', 
                fontSize: '1.5rem', 
                fontWeight: 700, 
                marginBottom: '0.75rem' 
              }}>
                Verification Failed
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                Invalid or expired verification link.
              </p>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '2rem' }}>
                Please request a new one.
              </p>
              <button 
                onClick={handleContinueToLogin}
                className="login-submit-btn"
                style={{ width: '100%' }}
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
